import { httpClient, publicHttpClient } from '@/lib/httpClient';
import { API_CONFIG } from '@/config/api';

export interface CategorySuggestion {
  category_id: string;
  category_name: string;
  confidence: number;
  reason: string;
  matched_keywords: string[];
}

export interface SmartSuggestionResponse {
  suggestions: CategorySuggestion[];
  primary_suggestion: CategorySuggestion | null;
}

export interface CategoryFeedback {
  job_title: string;
  suggested_category_id: string | null;
  selected_category_id: string;
  feedback_type: 'accepted' | 'rejected' | 'modified';
  user_id: string;
  timestamp: string;
}

export interface CategoryMetrics {
  accuracy_rate: number;
  total_suggestions: number;
  accepted_suggestions: number;
  rejected_suggestions: number;
  top_performing_categories: Array<{
    category_name: string;
    accuracy: number;
    suggestion_count: number;
  }>;
  improvement_areas: Array<{
    category_name: string;
    accuracy: number;
    common_issues: string[];
  }>;
}

export class SmartCategoryService {
  private getAuthToken(): string | undefined {
    return localStorage.getItem('auth_token') || undefined;
  }

  private getUserId(): string | undefined {
    return localStorage.getItem('user_id') || undefined;
  }

  /**
   * Get intelligent category suggestions based on job title and description
   */
  async getSuggestions(
    title: string, 
    description?: string
  ): Promise<SmartSuggestionResponse> {
    try {
      // Combine title and description into a single query string
      const query = description && description.trim() 
        ? `${title.trim()} ${description.trim()}` 
        : title.trim();

      const response = await publicHttpClient.get(API_CONFIG.ENDPOINTS.CATEGORY_SUGGEST, {
        params: {
          q: query,
          limit: '5'
        },
        auth: false // 🎯 EXPLICIT: This is a public endpoint
        // No token needed - this is a public endpoint
      });

      if (!response.success) {
        throw new Error(response.error || 'Failed to get category suggestions');
      }

      // Handle different possible response structures
      let data: SmartSuggestionResponse;
      
      if (Array.isArray(response.data)) {
        // If API returns array directly, wrap it in expected structure
        data = {
          suggestions: response.data,
          primary_suggestion: response.data.length > 0 ? response.data[0] : null
        };
      } else if (response.data && typeof response.data === 'object') {
        // If API returns object with suggestions property, map the structure
        const apiData = response.data as any;
        
        // Map API response to expected interface
        const mappedSuggestions = (apiData.suggestions || []).map((item: any) => ({
          category_id: item.id,
          category_name: item.name,
          confidence: item.confidence_score || 0,
          reason: item.description || '',
          matched_keywords: item.tags || []
        }));
        
        const mappedPrimary = apiData.primary_suggestion ? {
          category_id: apiData.primary_suggestion.id,
          category_name: apiData.primary_suggestion.name,
          confidence: apiData.primary_suggestion.confidence_score || 0,
          reason: apiData.primary_suggestion.description || '',
          matched_keywords: apiData.primary_suggestion.tags || []
        } : null;
        
        data = {
          suggestions: mappedSuggestions,
          primary_suggestion: mappedPrimary
        };
      } else {
        // Fallback to empty structure
        data = {
          suggestions: [],
          primary_suggestion: null
        };
      }
      
      console.log('✅ Smart suggestions received:', data);
      
      return data;
    } catch (error) {
      // Return empty suggestions on error (silently fail)
      return {
        suggestions: [],
        primary_suggestion: null
      };
    }
  }

  /**
   * Send feedback about category suggestions to improve the system
   */
  async sendFeedback(
    jobTitle: string,
    suggestedCategoryId: string | null,
    selectedCategoryId: string,
    feedbackType: 'accepted' | 'rejected' | 'modified'
  ): Promise<boolean> {
    const userId = this.getUserId();
    if (!userId) {
      console.warn('⚠️ Cannot send feedback: No user ID available');
      return false;
    }

    console.log('📊 Sending category feedback:', {
      jobTitle,
      suggestedCategoryId,
      selectedCategoryId,
      feedbackType
    });

    try {
      const feedback: CategoryFeedback = {
        job_title: jobTitle,
        suggested_category_id: suggestedCategoryId,
        selected_category_id: selectedCategoryId,
        feedback_type: feedbackType,
        user_id: userId,
        timestamp: new Date().toISOString()
      };

      const response = await httpClient.post(API_CONFIG.ENDPOINTS.CATEGORY_FEEDBACK, feedback, {
        token: this.getAuthToken()
      });

      if (response.success) {
        console.log('✅ Category feedback sent successfully');
        return true;
      } else {
        console.error('❌ Failed to send category feedback:', response.error);
        return false;
      }
    } catch (error) {
      console.error('❌ Category feedback error:', error);
      return false;
    }
  }

  /**
   * Get category suggestion performance metrics (for admin/analytics)
   */
  async getMetrics(): Promise<CategoryMetrics | null> {
    console.log('📈 Fetching category suggestion metrics...');
    
    try {
      const response = await httpClient.get(API_CONFIG.ENDPOINTS.CATEGORY_METRICS, {
        token: this.getAuthToken()
      });

      if (!response.success) {
        console.error('❌ Failed to get category metrics:', response.error);
        return null;
      }

      const metrics = response.data as CategoryMetrics;
      console.log('✅ Category metrics received:', metrics);
      
      return metrics;
    } catch (error) {
      console.error('❌ Category metrics error:', error);
      return null;
    }
  }

  /**
   * Determine feedback type based on suggestion and user selection
   */
  determineFeedbackType(
    suggestedCategoryId: string | null, 
    selectedCategoryId: string
  ): 'accepted' | 'rejected' | 'modified' {
    if (!suggestedCategoryId) {
      return 'modified'; // No suggestion was made, user selected manually
    }
    
    if (suggestedCategoryId === selectedCategoryId) {
      return 'accepted'; // User accepted the suggestion
    }
    
    return 'rejected'; // User chose a different category
  }
}

export const smartCategoryService = new SmartCategoryService();